/**
 * AuthGate — wraps the React tree. Boots Firebase on mount, subscribes
 * to auth-state changes, and renders SignInScreen until a user is
 * present. Once signed in, renders `children`.
 *
 * Loading vs. unauthenticated:
 *   - During the initial `bootFirebase()` promise we show a tiny
 *     placeholder (avoids a flash of the sign-in screen for already-
 *     signed-in users; Firebase reads the session from IndexedDB).
 *   - After boot, no user → SignInScreen. User present → children.
 */

import { useEffect, useState, type ReactNode } from "react";
import {
  bootFirebase,
  subscribe,
  getCurrentUser,
  sessionCookieReady,
} from "../lib/firebaseAuth";
import { installAuthFetch } from "../lib/api";
import { LandingScreen } from "./LandingScreen";

interface Props {
  children: ReactNode;
}

export function AuthGate({ children }: Props) {
  const [booted, setBooted] = useState(false);
  // We can't depend on User type from firebase here without re-exporting
  // it from firebaseAuth.ts; use a presence boolean instead.
  const [hasUser, setHasUser] = useState<boolean>(() => getCurrentUser() !== null);
  // Debounced "actually show the landing page" flag. Firebase briefly
  // reports no-user during token refresh / navigation transitions (e.g.
  // closing the in-chat doc viewer), which used to flash LandingScreen for
  // a frame before settling back to the chat. We only commit to showing
  // LandingScreen if the unauthenticated state PERSISTS — a real sign-out
  // stays null; a transient resolves within the grace window.
  const [showLanding, setShowLanding] = useState(false);

  useEffect(() => {
    // Install the fetch wrapper as early as possible so even the
    // initial API requests after sign-in carry the token.
    installAuthFetch();
    let unsub: (() => void) | null = null;
    bootFirebase().then(() => {
      setBooted(true);
      unsub = subscribe((user) => {
        if (user !== null) {
          // Wait for the `__session` cookie to be (re)established before
          // flagging "authenticated" — otherwise the first child paint can
          // iframe /career before the cookie exists and 401 until reload.
          sessionCookieReady().then(() => setHasUser(true));
        } else {
          setHasUser(false);
        }
      });
    });
    return () => {
      if (unsub) unsub();
    };
  }, []);

  useEffect(() => {
    if (booted && !hasUser) {
      // Wait out transient null states before showing the landing page.
      const t = setTimeout(() => setShowLanding(true), 600);
      return () => clearTimeout(t);
    }
    setShowLanding(false);
  }, [booted, hasUser]);

  if (!booted) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center bg-black text-zinc-500 text-xs font-mono">
        loading…
      </div>
    );
  }

  if (!hasUser && showLanding) {
    // Kit deploys default to the office-executive light theme (white + blue).
    // Operator-only forks can override via localStorage.
    const themeId =
      localStorage.getItem("plumbus-terminal-theme") || "office_executive";
    return <LandingScreen themeId={themeId} />;
  }

  // Authenticated, OR a transient unauthenticated blip we're waiting out:
  // keep rendering children so the chat doesn't flash to the landing page.
  return <>{children}</>;
}
